import * as Toast from "@radix-ui/react-toast";
import { useCallback, useMemo, useState, type PropsWithChildren } from "react";
import { CheckCircle2, AlertTriangle, AlertCircle, Info, X } from "lucide-react";
import clsx from "clsx";

import { ToastContext, type ToastApi, type ToastItem } from "./toast-context";
import styles from "./ToastProvider.module.css";

const VARIANT_ICON = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

export function ToastProvider({ children }: PropsWithChildren) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const api = useMemo<ToastApi>(() => {
    const push: ToastApi["push"] = (t) => {
      const id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2);
      setItems((prev) => [...prev, { id, ...t }]);
    };
    return {
      push,
      success: (title, description) => push({ title, description, variant: "success" }),
      error: (title, description) => push({ title, description, variant: "error" }),
      warning: (title, description) => push({ title, description, variant: "warning" }),
      info: (title, description) => push({ title, description, variant: "info" }),
    };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      <Toast.Provider swipeDirection="right" duration={5000}>
        {children}
        {items.map((t) => {
          const Icon = VARIANT_ICON[t.variant];
          return (
            <Toast.Root
              key={t.id}
              className={clsx(styles.toast, styles[t.variant])}
              onOpenChange={(open) => {
                if (!open) remove(t.id);
              }}
            >
              <div className={styles.icon} aria-hidden>
                <Icon size={18} />
              </div>
              <div className={styles.content}>
                <Toast.Title className={styles.title}>{t.title}</Toast.Title>
                {t.description && (
                  <Toast.Description className={styles.description}>
                    {t.description}
                  </Toast.Description>
                )}
              </div>
              <Toast.Close className={styles.close} aria-label="Dismiss">
                <X size={14} />
              </Toast.Close>
            </Toast.Root>
          );
        })}
        <Toast.Viewport className={styles.viewport} />
      </Toast.Provider>
    </ToastContext.Provider>
  );
}
